import React from 'react';
import { AlertOctagonIcon, ChevronRightIcon, ClipboardCheckIcon, FileCheck2Icon, HandshakeIcon, MilestoneIcon, UserPlusIcon } from 'lucide-react';
import { priorities, type PriorityCategory, type Urgency } from '../../data/perfectDay';
interface PrioritiesPanelProps {
  onOpenRecords: (recordSetId: string) => void;
}
const categoryIcons: Record<PriorityCategory, typeof UserPlusIcon> = {
  lead: UserPlusIcon,
  realtor: HandshakeIcon,
  preapproval: FileCheck2Icon,
  milestone: MilestoneIcon,
  task: ClipboardCheckIcon
};
const urgencyMeta: Record<Urgency, {
  label: string;
  badge: string;
  iconWrap: string;
  row: string;
  accent: string;
}> = {
  urgent: {
    label: 'Urgent',
    badge: 'bg-danger-50 text-danger-700 ring-danger-200',
    iconWrap: 'bg-danger-50 text-danger-600',
    row: 'hover:border-danger-200 hover:bg-danger-50/40',
    accent: 'bg-danger-500'
  },
  attention: {
    label: 'Attention',
    badge: 'bg-warn-50 text-warn-700 ring-warn-200',
    iconWrap: 'bg-warn-50 text-warn-600',
    row: 'hover:border-warn-200 hover:bg-warn-50/40',
    accent: 'bg-warn-500'
  },
  normal: {
    label: 'Scheduled',
    badge: 'bg-navy-50 text-navy-600 ring-navy-200',
    iconWrap: 'bg-brand-50 text-brand-600',
    row: 'hover:border-brand-200 hover:bg-brand-50/40',
    accent: 'bg-brand-400'
  }
};
export function PrioritiesPanel({
  onOpenRecords
}: PrioritiesPanelProps) {
  const urgentCount = priorities.filter(p => p.urgency === 'urgent').length;
  return <section aria-labelledby="todays-priorities-heading" className="rounded-2xl border border-navy-100 bg-white shadow-card" data-id="element-35" data-mp="38wmnymc3sqzf">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-navy-100 px-5 py-4" data-id="element-36" data-mp="3it7r124kbs0m">
        <div data-id="element-37" data-mp="2v3dfcnvzc9hy">
          <h2 id="todays-priorities-heading" className="text-lg font-semibold text-navy-900" data-id="element-38" data-mp="2v2vbp66zmt00">
            Today’s Priorities
          </h2>
          <p className="mt-1 text-sm text-navy-500" data-id="element-39" data-mp="u1nqthowzpnp">
            Ranked by revenue impact and time sensitivity
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-50 px-3 py-1.5 text-xs font-semibold text-danger-700 ring-1 ring-danger-200" data-id="element-40" data-mp="yzgy6e1yeppk">
          <AlertOctagonIcon className="h-4 w-4" aria-hidden="true" data-id="element-41" data-mp="17skhh2za5ws4" />
          {urgentCount} urgent
        </span>
      </div>

      <ul className="divide-y divide-navy-100" data-id="element-42" data-mp="b3bify5tujt6">
        {priorities.map(priority => {
        const Icon = categoryIcons[priority.category];
        const meta = urgencyMeta[priority.urgency];
        return <li key={priority.id} data-id="element-43" data-mp="k2gaqmqspgww">
              <button type="button" onClick={() => onOpenRecords(priority.recordSetId)} aria-label={`View ${priority.count} ${priority.label}`} className={`group relative flex w-full items-start gap-3 border-l-2 border-transparent px-4 py-4 text-left transition-colors sm:gap-4 sm:px-5 ${meta.row}`} data-id="element-44" data-mp="k5kpge1vzv1t">
                <span className={`absolute left-0 top-0 h-full w-1 ${meta.accent}`} aria-hidden="true" data-id="element-45" data-mp="2akn1p6ttamju" />
                <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${meta.iconWrap}`} data-id="element-46" data-mp="20utmlvugnpau">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-47" data-mp="1ws70mbkh0ylk" />
                </span>

                <div className="min-w-0 flex-1" data-id="element-48" data-mp="1rsu5z4ncyn8f">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-49" data-mp="1mvkgxq3vsdl4">
                    <p className="text-base font-semibold text-navy-900" data-id="element-50" data-mp="1hwhgkiyuivjh">
                      <span className="mr-1.5 text-lg font-bold" data-id="element-51" data-mp="1hykc1iirpgvl">
                        {priority.count}
                      </span>
                      {priority.label}
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ${meta.badge}`} data-id="element-52" data-mp="3c8fli8q4h59k">
                      {meta.label}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-navy-500" data-id="element-53" data-mp="3k9dhe6utuwf2">
                    {priority.description}
                  </p>
                </div>

                <span className="ml-auto hidden shrink-0 items-center gap-1 self-center rounded-lg border border-navy-200 px-3 py-2 text-sm font-semibold text-navy-700 group-hover:border-navy-300 group-hover:bg-white sm:inline-flex" data-id="element-54" data-mp="3acikfse7d42w">
                  View
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-55" data-mp="31ef192qowje9" />
                </span>
                <ChevronRightIcon className="mt-3 h-5 w-5 shrink-0 self-center text-navy-300 sm:hidden" aria-hidden="true" data-id="element-56" data-mp="2mluyi37gcdxc" />
              </button>
            </li>;
      })}
      </ul>
    </section>;
}